import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "../lib/axios";
import BreadCrumb from "../components/BreadCrumb";
import { motion } from "framer-motion";
import YoutubeModal from "../components/YoutubeModal";
import { getYoutubeVideoId } from "../utils/youtube";
import {
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
} from "react-icons/fa";
import BrandEndorsementQuotationModal from "../components/BrandEndorsementQuotationModal";

export const SocialIcon = ({ platform, followers }) => {
  const icons = {
    instagram: <FaInstagram className="text-[#C13584] text-2xl" />,
    youtube: <FaYoutube className="text-[#FF0000] text-2xl" />,
    facebook: <FaFacebook className="text-[#3b5998] text-2xl" />,
    twitter: <FaTwitter className="text-[#1DA1F2] text-2xl" />,
    linkedin: <FaLinkedin className="text-[#0e76a8] text-2xl" />,
  };

  return (
    <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
      {icons[platform]}
      <span className="font-medium">{followers || "Followers"}</span>
    </div>
  );
};

const SAMPLE_BRAND_ENDORSEMENT = {
  id: "67c83c3c5edf6f5114b48a7b",
  name: "Vineeta",
  image: {
    id: "67c83c1b5edf6f5114b48a42",
    filename: "vineeta.jpg",
    mimeType: "image/jpeg",
    filesize: 53873,
    width: 800,
    height: 345,
    focalX: 50,
    focalY: 50,
    createdAt: "2025-03-05T11:57:15.562Z",
    updatedAt: "2025-03-05T11:57:15.562Z",
    url: "https://admin.indorinama.in/api/media/file/vineeta.jpg",
    thumbnailURL: null,
  },
  platforms: ["instagram", "facebook", "twitter"],
  followers: 896,
  tier: "tier1",
  rank: 0,
  minimum_billing_amount: 10000,
  options: [
    {
      name: "Vineeta",
      image: "67c83c285edf6f5114b48a5a",
      offer_rate: 10000,
      price_on_request: true,
      minimum_billing: 10000,
      price_units: ["6790b9c85c2575fb937387e9"],
      id: "67c83c1c6052a3bb95debccf",
    },
  ],
  createdAt: "2025-03-05T11:57:48.173Z",
  updatedAt: "2025-03-20T14:21:09.128Z",
  socials: [
    {
      platform: "youtube",
      followers: "234 M",
      id: "67cdeebebca0fd7af20f8948",
    },
    {
      platform: "twitter",
      followers: "23424m",
      id: "67cdeec8bca0fd7af20f8949",
    },
    {
      platform: "linkedin",
      id: "67d32c2dfcd01a3e77709b3f",
    },
    {
      platform: "facebook",
      id: "67d32c34fcd01a3e77709b40",
    },
  ],
  youtube_video: "https://www.youtube.com/watch?v=qoUForfbcZM",
  slug: "vineeta",
  verified: false,
  internal_price: 0,
};

const BrandEndorsementDetail = () => {
  const navigate = useNavigate();
  const [endorsement, setEndorsement] = useState(null);
  const [loading, setLoading] = useState(true);
  const { slug } = useParams();
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  // Extract slug from query params
  // const searchParams = new URLSearchParams(location.search);
  // const slug = searchParams.get("slug");

  useEffect(() => {
    const fetchEndorsementData = async () => {
      setLoading(true);

      // For testing purposes, use sample data directly
      if (!slug) {
        setEndorsement(SAMPLE_BRAND_ENDORSEMENT);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `/api/brand-endorsements?where[slug][equals]=${slug}`
        );

        if (
          response.status === 200 &&
          response.data.docs &&
          response.data.docs.length > 0
        ) {
          setEndorsement(response.data.docs[0]);
        } else {
          // If no results found, use sample data for demo
          setEndorsement(SAMPLE_BRAND_ENDORSEMENT);
        }
      } catch (err) {
        console.error("Error fetching brand endorsement data:", err);
        // Use sample data if API fails
        setEndorsement(SAMPLE_BRAND_ENDORSEMENT);
      } finally {
        setLoading(false);
      }
    };

    fetchEndorsementData();
  }, [slug]);

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!endorsement) {
    return (
      <div>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Brand endorsement not found
          </h2>
          <button
            onClick={() => navigate("/brand-endorsement")}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Brand Endorsements
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const youtubeVideoId = endorsement.youtube_video
    ? getYoutubeVideoId(endorsement.youtube_video)
    : null;

  const breadcrumbItems = [
    { name: "Home", url: "/" },
    { name: "Brand Endorsement", url: "/brand-endorsement" },
    { name: endorsement.name, url: "" },
  ];

  return (
    <div>
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <BreadCrumb items={breadcrumbItems} />

        <div className="bg-white rounded-xl shadow-lg overflow-hidden my-8">
          <div className="md:flex">
            {/* Left side - Image */}
            <div className="md:w-1/3 relative">
              <img
                src={
                  endorsement.image.url ||
                  `${process.env.REACT_APP_BACKEND_URL}/media/${endorsement.image.filename}`
                }
                alt={endorsement.name}
                className="w-full h-full object-cover object-center"
              />
              {endorsement.verified_id && (
                <a
                  href={endorsement?.verified_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
                >
                  {endorsement.verified_id}
                </a>
              )}
              {endorsement.verified && (
                <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Verified
                </div>
              )}
            </div>

            {/* Right side - Details */}
            <div className="md:w-2/3 p-6">
              <div className="flex items-center">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">
                  {endorsement.name}
                </h1>

                {endorsement.verified && (
                  <div className="ml-2 -mt-4">
                    <svg
                      aria-label="Verified"
                      className="x1lliihq x1n2onr6"
                      fill="rgb(0, 149, 246)"
                      height="24"
                      role="img"
                      viewBox="0 0 40 40"
                      width="24"
                    >
                      <title>Verified</title>
                      <path
                        d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z"
                        fillRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                )}
              </div>
                {/* Social Media */}
                {endorsement?.socials && endorsement?.socials?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Social Media Presence
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {endorsement?.socials?.map((social, index) => (
                        <SocialIcon
                          key={index}
                          platform={social.platform}
                          followers={social.followers}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Brand Endorsement Details */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                    Brand Endorsement Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-700">
                        <span className="font-medium">Category:</span>{" "}
                        {endorsement.catgory}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700">
                        <span className="font-medium">Followers:</span>{" "}
                        {endorsement.followers}
                      </p>
                    </div>
                  </div>
                </div>

                {/* YouTube Video */}
                {youtubeVideoId && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Featured Content
                    </h3>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                      className="relative cursor-pointer bg-gray-200 rounded-lg overflow-hidden"
                      onClick={() => setShowYoutubeModal(true)}
                    >
                      <img
                        src={`https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`}
                        alt="YouTube Thumbnail"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                          <div className="w-0 h-0 border-t-8 border-b-8 border-l-12 border-t-transparent border-b-transparent border-l-white ml-1"></div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Contact/Quote Button */}
                <div className="mt-8">
                  <button
                    onClick={() => setShowQuotationModal(true)}
                    className="px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-lg"
                  >
                    Get a Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* YouTube Video Modal */}
      {showYoutubeModal && youtubeVideoId && (
        <YoutubeModal
          isOpen={showYoutubeModal}
          onClose={() => setShowYoutubeModal(false)}
          videoId={youtubeVideoId}
        />
      )}

      {/* Quotation Modal */}
      <BrandEndorsementQuotationModal
        isOpen={showQuotationModal}
        onClose={() => setShowQuotationModal(false)}
        brandEndorsementName={endorsement?.name}
        type="brand-endorsement"
      />

      <Footer />
    </div>
  );
};

export default BrandEndorsementDetail;
